import { NavigationList, NavigationListItem } from "../types/NavigationList.ts";
import { SolutionOptionKey } from "@labs/shared/types/Solution.ts";
import { PlanComponentPageKey } from "@labs/shared/types/PlanComponentPageKey.ts";
import cutWasteImg from "../assets/banners/cutWaste.png";
import buyCleanImg from "../assets/banners/buy.png";
import electrifyImg from "../assets/banners/electrify.png";
import lessCommonImg from "../assets/banners/lessCommon.png";
import { planComponentPageKeyHrefMap } from "@labs/shared/data/planComponentPageKeyHrefMap.ts";

export const solutionsNavigationData: NavigationList = [
  {
    name: "Scope 1",
    key: "scope1",
    description: "Scope 1 description",
    children: [
      {
        name: "Cut Energy Waste",
        key: PlanComponentPageKey.Scope1CutEnergyWaste,
        href: "/plan/entry/scope1CutEnergyWaste",
        category: "Scope 1",
        heroBackgroundImage: cutWasteImg,
        description:
          "Strengthen your business by investing in energy-efficient equipment and controls to reduce emissions, cut operating costs, and modernize your facilities.",
        solutions: [
          SolutionOptionKey.EnergyEfficiencyAndHeatRecoveryNaturalGas,
          SolutionOptionKey.EnergyEfficiencyAndHeatRecoveryGasoline,
          SolutionOptionKey.EnergyEfficiencyAndHeatRecoveryDiesel,
          SolutionOptionKey.EnergyEfficiencyAndHeatRecoveryPropane,
          SolutionOptionKey.EnergyEfficiencyAndHeatRecoveryFuelOil,
        ],
      },
      {
        name: "Electrify",
        key: PlanComponentPageKey.Scope1Electrify,
        href: "/plan/entry/scope1Electrify",
        category: "Scope 1",
        heroBackgroundImage: electrifyImg,
        description:
          "Convert energy systems from fossil fuels to electricity. As the grid incorporates more renewable sources, electrification lowers overall emissions. The efficiency of electric technologies is generally much higher than fossil fuel-based equipment. Our calculations conservatively assume a doubling of system efficiency.",
        solutions: [
          SolutionOptionKey.StationaryElectrificationNaturalGas,
          SolutionOptionKey.StationaryElectrificationPropane,
          SolutionOptionKey.StationaryElectrificationFuelOil,
          SolutionOptionKey.MobileElectrificationGasoline,
          SolutionOptionKey.MobileElectrificationDiesel,
        ],
      },
      {
        name: "Buy Clean",
        key: PlanComponentPageKey.Scope1BuyClean,
        href: "/plan/entry/scope1BuyClean",
        category: "Scope 1",
        heroBackgroundImage: buyCleanImg,
        description:
          "Buy clean fuels to provide thermal energy with fewer emissions than fossil fuels. Clean solutions include zero-carbon fuels like green hydrogen or ammonia, and biofuels.",
        solutions: [
          SolutionOptionKey.LowNoCarbonFuelSwitchingNaturalGas,
          SolutionOptionKey.LowNoCarbonFuelSwitchingPropane,
          SolutionOptionKey.LowNoCarbonFuelSwitchingFuelOil,
          SolutionOptionKey.LowNoCarbonFuelSwitchingGasoline,
          SolutionOptionKey.LowNoCarbonFuelSwitchingDiesel,
        ],
      },
      {
        name: "Less Common Solutions",
        key: PlanComponentPageKey.Scope1LessCommonSolutions,
        href: "/plan/entry/scope1LessCommonSolutions",
        category: "Scope 1",
        heroBackgroundImage: lessCommonImg,
        description:
          "These solutions are less common because they typically have a small impact for most organizations, or they are not cost-effective with today’s technologies.",
        solutions: [
          SolutionOptionKey.CarbonCaptureAndSequestrationCarbon,
          SolutionOptionKey.EliminateFugitiveEmissionsCarbon,
          SolutionOptionKey.OffsetsCarbon,
          SolutionOptionKey.RefrigerantLeaksRefrigerants,
          SolutionOptionKey.LowGwpRefrigerantsRefrigerants,
        ],
      },
    ],
  },
  {
    name: "Scope 2",
    key: "scope2",
    description: "Scope 2 description",
    children: [
      {
        name: "Cut Energy Waste",
        key: PlanComponentPageKey.Scope2CutEnergyWaste,
        href: planComponentPageKeyHrefMap[
          PlanComponentPageKey.Scope2CutEnergyWaste
        ],
        category: "Scope 2",
        heroBackgroundImage: cutWasteImg,
        description:
          "Strengthen your business by investing in energy-efficient equipment and controls to reduce emissions, cut operating costs, and modernize your facilities.",
        solutions: [
          SolutionOptionKey.EnergyEfficiencyAndHeatRecoveryElectricity,
        ],
      },
      {
        name: "Buy Clean",
        key: PlanComponentPageKey.Scope2BuyClean,
        href: "/plan/entry/scope2BuyClean",
        category: "Scope 2",
        heroBackgroundImage: buyCleanImg,
        description:
          "Buy clean electricity to power your electric systems and equipment. A wide range of solutions from onsite generation to renewable energy credits are available based on physical, geographical, and economic constraints. All solutions on this page result in a decrease in emissions from purchased electricity, so the total of all sliders cannot exceed 100%.",
        solutions: [
          SolutionOptionKey.CoGenerationElectricity,
          SolutionOptionKey.RenewablePurchaseFromUtilityElectricity,
          SolutionOptionKey.RenewableEnergyAsPpaVppaElectricity,
          SolutionOptionKey.RenewableCreditsRecsElectricity,
          SolutionOptionKey.OnsiteSolarOrRenewableEnergyGenerationElectricity,
        ],
      },
    ],
  },
  {
    name: "Scope 3",
    key: "scope3",
    href: "/plan/scope3", // different URL scheme so we can use a different container
    description: "Scope 3 is complex. Schedule a consultation to learn more.",
    children: [],
  },
];

const mapByKey = (
  navigationData: NavigationList,
): Record<string, NavigationListItem> => {
  const result: Record<string, NavigationListItem> = {};

  const recursiveMap = (data: NavigationList) => {
    data.forEach((item) => {
      result[item.key] = item;
      if (item.children) {
        recursiveMap(item.children);
      }
    });
  };

  recursiveMap(navigationData);
  return result;
};

export const solutionsNavigationDataByKey = mapByKey(solutionsNavigationData);

const addHrefs = (data: NavigationList): NavigationList =>
  data.map((item) => {
    const newItem: NavigationListItem = {
      ...item,
      href: item.href,
      children: item.children ? addHrefs(item.children) : undefined,
    };
    return newItem;
  });

export const solutionsNavigationDataWithHrefs: NavigationList = addHrefs(
  solutionsNavigationData,
);

export const orderedSolutionPages = [
  "scope1CutEnergyWaste",
  "scope1Electrify",
  "scope1BuyClean",
  "scope1LessCommonSolutions",
  "scope2CutEnergyWaste",
  "scope2BuyClean",
];
